import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  CheckCircle, 
  XCircle, 
  Shield, 
  Calendar,
  FileText,
  User,
  AlertCircle,
  Download,
  Fingerprint,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContractVerificationPage = () => {
  const { contractId } = useParams();
  const [contract, setContract] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    verifyContract();
  }, [contractId]);

  const verifyContract = async () => {
    try {
      setLoading(true);
      
      // جلب بيانات العقد (API عام بدون مصادقة)
      const contractResponse = await axios.get(
        `${BACKEND_URL}/api/contracts/public/verify/${contractId}`
      ).catch(() => null);

      if (!contractResponse || !contractResponse.data) {
        setError('العقد غير موجود أو غير صالح للتحقق');
        return;
      }

      setContract(contractResponse.data);

      // محاولة جلب التوقيعات (قد لا تنجح إذا كانت محمية)
      try {
        const sigResponse = await axios.get(
          `${BACKEND_URL}/api/signatures/contract/${contractId}/signatures`
        );
        
        if (sigResponse && sigResponse.data) {
          setSignatures(sigResponse.data.signatures || []);
        }
      } catch (sigErr) {
        // التوقيعات محمية - سنعتمد على حالة العقد فقط
        console.log('لا يمكن جلب التوقيعات (محمية)');
      }

      // تحديد حالة التحقق
      if (contractResponse.data.status === 'signed') {
        setVerified(true);
      }

    } catch (err) {
      console.error('خطأ في التحقق:', err);
      setError('حدث خطأ في التحقق من العقد');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير متوفر';
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#C8A870] mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">جاري التحقق من العقد...</p>
        </div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4" dir="rtl">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="bg-red-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-12 h-12 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">فشل التحقق</h1>
          <p className="text-gray-600 mb-6">{error || 'لم يتم العثور على العقد'}</p>
          <a
            href="https://wazin.sa"
            className="inline-block bg-gradient-to-r from-[#C8A870] to-[#b8986a] text-white px-8 py-3 rounded-lg font-bold hover:shadow-lg transition-all"
          >
            العودة للموقع الرئيسي
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12 px-4" dir="rtl">
      <div className="max-w-4xl mx-auto">
        
        {/* Header with Verification Status */}
        <div className={`rounded-2xl shadow-2xl p-8 mb-6 ${
          verified 
            ? 'bg-gradient-to-br from-green-500 to-green-600' 
            : 'bg-gradient-to-br from-yellow-500 to-yellow-600'
        }`}>
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white rounded-full p-4">
              {verified ? (
                <CheckCircle className="w-16 h-16 text-green-600" />
              ) : (
                <Clock className="w-16 h-16 text-yellow-600" />
              )}
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-white text-center mb-2">
            {verified ? 'عقد موثّق وموقّع ✓' : 'عقد قيد التوقيع'}
          </h1>
          <p className="text-white text-center text-lg opacity-90">
            {verified 
              ? 'تم التحقق من أصالة العقد بنجاح. هذا العقد موقّع من جميع الأطراف وله قوة قانونية ملزمة.'
              : 'هذا العقد في انتظار توقيع أحد الأطراف.'}
          </p>
        </div>

        {/* Contract Details */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-[#C8A870]">
            <Shield className="w-8 h-8 text-[#C8A870]" />
            <h2 className="text-2xl font-bold text-[#1e3a8a]">معلومات العقد</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* رقم العقد */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <FileText className="w-5 h-5" />
                <span className="text-sm font-medium">رقم العقد</span>
              </div>
              <p className="font-mono text-xl font-bold text-gray-900">
                {contract.contract_number}
              </p>
            </div>

            {/* تاريخ الإنشاء */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <Calendar className="w-5 h-5" />
                <span className="text-sm font-medium">تاريخ الإنشاء</span>
              </div>
              <p className="text-lg font-bold text-gray-900">
                {contract.field_data?.contract_date || formatDate(contract.created_at)}
              </p>
            </div>

            {/* اسم العميل */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <User className="w-5 h-5" />
                <span className="text-sm font-medium">اسم العميل</span>
              </div>
              <p className="text-lg font-bold text-gray-900">
                {contract.field_data?.client_name || 'غير متوفر'}
              </p>
            </div>

            {/* الحالة */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm font-medium">حالة العقد</span>
              </div>
              <p className={`text-lg font-bold ${
                contract.status === 'signed' ? 'text-green-600' : 'text-yellow-600'
              }`}>
                {contract.status === 'signed' ? 'موقّع بالكامل' : 'قيد المعالجة'}
              </p>
            </div>
          </div>

          {/* معرّف التحقق (Hash) */}
          <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border-2 border-blue-200">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <Fingerprint className="w-5 h-5" />
              <span className="text-sm font-bold">معرّف التحقق (Hash)</span>
            </div>
            <p className="font-mono text-sm text-gray-700 break-all">
              {contract.id}
            </p>
          </div>
        </div>

        {/* Signatures Section */}
        {signatures.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-[#C8A870]">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <h2 className="text-2xl font-bold text-[#1e3a8a]">التوقيعات الإلكترونية</h2>
            </div>

            <div className="space-y-4">
              {signatures.map((sig, index) => (
                <div key={sig.id} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border-2 border-green-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-bold text-gray-900">
                          {sig.signature_type === 'client' ? 'توقيع العميل' : 'توقيع المحامي'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-1">
                        <span className="font-medium">الاسم:</span> {sig.signer_name}
                      </p>
                      <p className="text-sm text-gray-700 mb-1">
                        <span className="font-medium">البريد:</span> {sig.signer_email}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">تاريخ التوقيع:</span> {formatDate(sig.signed_at)}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        <span className="font-medium">IP:</span> {sig.ip_address} | 
                        <span className="font-medium"> الجهاز:</span> {sig.device_type}
                      </p>
                    </div>
                    
                    {sig.is_verified && (
                      <div className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                        موثّق ✓
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security Notice */}
        <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">ملاحظة أمنية</h3>
              <p className="text-sm text-blue-800 leading-relaxed">
                هذا العقد محمي بتقنيات التشفير المتقدمة. يمكن التحقق من أصالته في أي وقت عبر مسح رمز QR الموجود 
                في نهاية المستند. أي تعديل على العقد بعد التوقيع سيؤدي إلى بطلان التحقق الإلكتروني.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-600 mb-4">صادر من منصة وازن الرقمية للمحاماة والاستشارات القانونية</p>
          <a
            href="https://wazin.sa"
            className="inline-block bg-gradient-to-r from-[#C8A870] to-[#b8986a] text-white px-8 py-3 rounded-lg font-bold hover:shadow-lg transition-all"
          >
            زيارة الموقع الرئيسي
          </a>
        </div>

      </div>
    </div>
  );
};

export default ContractVerificationPage;
